"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, Loader2, X } from "lucide-react";
import { SALES_TERMS_VERSION } from "@/lib/purchases/legalVersion";

const checkoutProducts: Record<string, { name: string; price: string; format: string }> = {
  "wyniki-spolek-bez-fomo": { name: "Wyniki spółek bez FOMO", price: "29 zł", format: "e-book PDF" },
  "checklista-przed-wynikami-spolki": { name: "Checklista przed wynikami spółki", price: "29 zł", format: "szablon Google Sheets; wymaga konta Google do własnej kopii" },
  "poranny-pulpit-inwestora": { name: "Poranny pulpit inwestora", price: "39 zł", format: "szablon Notion; wymaga konta Notion do własnej kopii" },
};

type CheckoutButtonProps = {
  children: React.ReactNode;
  className?: string;
  productSlug?: string;
};

export function CheckoutButton({
  children,
  className = "",
  productSlug = "wyniki-spolek-bez-fomo",
}: CheckoutButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [earlyDeliveryRequested, setEarlyDeliveryRequested] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const product = checkoutProducts[productSlug];

  useEffect(() => {
    if (!isOpen) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isLoading) setIsOpen(false);
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled])',
      ));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onEscape);
    return () => {
      window.removeEventListener("keydown", onEscape);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [isOpen, isLoading]);

  async function startCheckout() {
    if (!termsAccepted || !earlyDeliveryRequested || !product) return;
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          productSlug,
          termsVersion: SALES_TERMS_VERSION,
          termsAccepted,
          earlyDeliveryRequested,
        }),
      });
      const data = (await response.json()) as { url?: string; error?: string };

      if (!response.ok || !data.url) {
        throw new Error(data.error ?? "Missing Stripe Checkout URL.");
      }

      window.location.assign(data.url);
    } catch (checkoutError) {
      console.error("Checkout start failed:", checkoutError);
      setError(
        "Nie udało się rozpocząć płatności. Spróbuj ponownie lub skontaktuj się z nami.",
      );
      setIsLoading(false);
    }
  }

  return (
    <div className={className}>
      <button
        className="group inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#3498db] px-5 py-3 text-sm font-semibold text-[#07111d] shadow-[0_0_30px_rgba(52,152,219,0.28)] transition duration-200 hover:bg-[#54afe9] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3498db] disabled:cursor-wait disabled:opacity-80"
        disabled={isLoading}
        onClick={() => { setError(""); setIsOpen(true); }}
        type="button"
      >
        {isLoading ? (
          <>
            <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            Przekierowuję do płatności...
          </>
        ) : (
          <>
            {children}
            <ArrowRight
              aria-hidden="true"
              className="size-4 transition group-hover:translate-x-0.5"
            />
          </>
        )}
      </button>
      {error ? (
        <p className="mt-3 text-xs leading-5 text-red-200">{error}</p>
      ) : null}
      {isOpen && product ? createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget && !isLoading) setIsOpen(false);
        }}>
          <div aria-labelledby="checkout-summary-title" aria-modal="true" className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-md border border-white/20 bg-[#0e1117] p-5 text-slate-100 shadow-2xl sm:p-7" ref={dialogRef} role="dialog">
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-lg font-semibold" id="checkout-summary-title">Przed płatnością</h2>
              <button aria-label="Zamknij" className="rounded p-1 text-slate-300 hover:text-white" disabled={isLoading} onClick={() => setIsOpen(false)} ref={closeRef} type="button"><X aria-hidden="true" className="size-5" /></button>
            </div>
            <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-slate-400">Produkt</dt><dd>{product.name}</dd>
              <dt className="text-slate-400">Format</dt><dd>{product.format}</dd>
              <dt className="text-slate-400">Łącznie</dt><dd className="font-semibold text-[#FFD700]">{product.price}</dd>
              <dt className="text-slate-400">Dostawa</dt><dd>E-mail po potwierdzeniu płatności, bez dodatkowej opłaty.</dd>
              <dt className="text-slate-400">Sprzedawca</dt><dd>Bartosz Kowalczyk, RynkoweFakty</dd>
            </dl>
            <p className="mt-5 text-sm leading-6 text-slate-300">Konsument może odstąpić od umowy w ciągu 14 dni. Rozpoczęcie dostawy nie odbiera tego prawa. Szczegóły są w <a className="text-[#54afe9] underline" href="/regulamin" target="_blank" rel="noopener noreferrer">regulaminie</a> (wersja {SALES_TERMS_VERSION}) i <a className="text-[#54afe9] underline" href="/polityka-prywatnosci" target="_blank" rel="noopener noreferrer">polityce prywatności</a>.</p>
            <label className="mt-5 flex items-start gap-3 text-sm leading-6"><input checked={termsAccepted} className="mt-1 size-4 accent-[#3498db]" onChange={(event) => setTermsAccepted(event.target.checked)} type="checkbox" /><span>Zapoznałem(-am) się z regulaminem sprzedaży i informacją o prawie odstąpienia.</span></label>
            <label className="mt-4 flex items-start gap-3 text-sm leading-6"><input checked={earlyDeliveryRequested} className="mt-1 size-4 accent-[#3498db]" onChange={(event) => setEarlyDeliveryRequested(event.target.checked)} type="checkbox" /><span>Proszę o rozpoczęcie dostawy produktu cyfrowego po potwierdzeniu płatności. Rozumiem, że zachowuję 14-dniowe prawo odstąpienia.</span></label>
            {error ? <p className="mt-4 text-sm text-red-200" role="alert">{error}</p> : null}
            <button className="mt-6 flex min-h-12 w-full items-center justify-center gap-2 rounded-md bg-[#3498db] px-4 py-3 font-semibold text-[#07111d] disabled:cursor-not-allowed disabled:opacity-50" disabled={isLoading || !termsAccepted || !earlyDeliveryRequested} onClick={startCheckout} type="button">
              {isLoading ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
              Przejdź do płatności: {product.price}
            </button>
            <p className="mt-3 text-xs leading-5 text-slate-400">Płatność obsługuje Stripe. Dokumenty i potwierdzenie zamówienia otrzymasz e-mailem.</p>
          </div>
        </div>, document.body
      ) : null}
    </div>
  );
}
